/**
 * AES-GCM encryption for chat history stored in IndexedDB.
 *
 * Security model:
 * - Keys are randomly generated using crypto.subtle.generateKey()
 * - Keys are stored as non-extractable CryptoKey objects in IndexedDB
 * - Different consumerIDs have different keys — User B cannot decrypt User A's data
 * - Keys cannot be extracted or exported — even with XSS, attacker cannot copy key offline
 */

import { idbGetKey, idbSetKey } from './indexedDBStorage';

/**
 * Get or create a non-extractable CryptoKey for the given consumer.
 * Keys are stored in IndexedDB and cannot be exported or read by JavaScript.
 */
async function getOrCreateKey(consumerId: string): Promise<CryptoKey> {
  const keyId = await chatStorageKey(consumerId);

  // Check if key already exists
  const existingKey = await idbGetKey(keyId);
  if (existingKey) {
    return existingKey;
  }

  // Generate a new random non-extractable key
  const newKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // extractable = false — browser enforces this, JS cannot read key bytes
    ['encrypt', 'decrypt']
  );

  // Store the key
  await idbSetKey(keyId, newKey);
  return newKey;
}

/**
 * Encrypt chat history data using AES-GCM.
 * Returns: IV (12 bytes) + Ciphertext
 */
export async function encryptChatHistory(data: object, consumerId: string): Promise<Uint8Array> {
  const key = await getOrCreateKey(consumerId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  // Prepend IV (12 bytes) to ciphertext
  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.byteLength);
  return combined;
}

/**
 * Decrypt chat history data using AES-GCM.
 * Expects: IV (12 bytes) + Ciphertext
 */
export async function decryptChatHistory(cipherData: Uint8Array, consumerId: string): Promise<object> {
  const key = await getOrCreateKey(consumerId);
  const iv = cipherData.slice(0, 12);
  const ciphertext = cipherData.slice(12);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return JSON.parse(new TextDecoder().decode(plaintext));
}

/** Returns the storage key scoped to this user (consumerId is hashed for privacy). */
export async function chatStorageKey(consumerId: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(consumerId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return `marriott-ai-chat:${hashHex}`;
}
